import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';

const RentCollectionReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/reports/rent-collection/?month=${filters.month}&year=${filters.year}`);

      if (response.data && response.data.success) {
        setInvoices(response.data.data || []);
        setSummary(response.data.summary || null);
        toastSuccess(`Loaded ${(response.data.data || []).length} records`);
      } else {
        toastError('Failed to load report');
      }
    } catch (error) {
      console.error('Error loading rent collection report:', error);
      toastError('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: parseInt(value) }));
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { background: '#15803d', color: '#ffffff', border: '1px solid #16a34a' },
      sent: { background: '#1d4ed8', color: '#ffffff', border: '1px solid #2563eb' },
      partially_paid: { background: '#b45309', color: '#ffffff', border: '1px solid #d97706' },
      draft: { background: '#475569', color: '#ffffff', border: '1px solid #64748b' },
      overdue: { background: '#b91c1c', color: '#ffffff', border: '1px solid #dc2626' },
      cancelled: { background: '#6b7280', color: '#ffffff', border: '1px solid #9ca3af' }
    };
    const style = styles[status] || styles.draft;
    return (
      <span style={{
        ...style,
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 600,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap'
      }}>
        {status ? status.replace('_', ' ') : 'N/A'}
      </span>
    );
  };

  // Filter data by status (partially_paid and sent are considered unpaid)
  const filteredInvoices = statusFilter
    ? invoices.filter(inv => {
        if (statusFilter === 'paid') return inv.status === 'paid';
        if (statusFilter === 'unpaid') return inv.status !== 'paid';
        return true;
      })
    : invoices;

  const columns = [
    {
      Header: 'Invoice #',
      accessor: 'invoice_number',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (value || 'N/A')
      ),
      exportValue: (row) => row.pg_name || 'N/A'
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => (
        row.building_id ? (
          <RTButton
            type="link"
            onClick={() => handleBuildingClick(row.building_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (value || 'N/A')
      ),
      exportValue: (row) => row.building_name || 'N/A'
    },
    {
      Header: 'Invoice Items',
      accessor: 'items_text',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Amount',
      accessor: 'total_amount',
      Cell: ({ value }) => formatAmount(value),
      exportValue: (row) => row.total_amount || 0
    },
    {
      Header: 'Paid',
      accessor: 'amount_paid',
      Cell: ({ value }) => formatAmount(value),
      exportValue: (row) => row.amount_paid || 0
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value),
      exportValue: (row) => row.status || ''
    },
    {
      Header: 'Payment Provider',
      accessor: 'payment_provider',
      Cell: ({ value }) => value || 'N/A'
    }
  ];

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear - 2; year <= currentYear + 1; year++) {
    yearOptions.push({ value: year, label: year.toString() });
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Rent Collection Report</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <RTButton
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/reports')}
            >
              ← Back
            </RTButton>
          </div>
        </div>

        {/* Month/Year Filter */}
        <div className="rt-section" style={{ flex: 'none' }}>
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Month</label>
              <select
                className="form-select"
                value={filters.month}
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Year</label>
              <select
                className="form-select"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                {yearOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <RTButton variant="primary" onClick={loadReport} disabled={loading}>
                Apply
              </RTButton>
            </div>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Invoices</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}>{summary.total_invoices}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Paid</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}>{summary.paid_count}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Unpaid</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}>{summary.unpaid_count}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Amount</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(summary.total_amount)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Paid</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(summary.total_paid)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Unpaid</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(summary.total_unpaid)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Adjustments</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(summary.total_adjustments)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Collected Rent</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(summary.collected_rent)}</div>
            </div>
          </div>
        )}

        {/* Status Filter + Table */}
        <div className="rt-section">
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center">
              <RTSpinner message="Loading rent collection report..." />
            </div>
          ) : (
            <Table
              columns={columns}
              data={filteredInvoices}
              title="Rent Collection Report"
              exportable={true}
              noDataText="No rent collection data found for the selected period"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RentCollectionReport;
